// object-fall-janus.ts — Janus R2-2: an object's fall damage through LANE A's damageTypeRoll() (the one damage resolver).
//
// PHP runs a falling object's damage through janus() with the object as BOTH caster and target
// (apply_fall_damage_to_inventory_in_feet() → 'Take Fall Damage' → damage_roll → damage_type_roll → calc_total →
// apply_damage_to_target). Lane A has ported that roll (ability/rolls/damage-type-roll.ts) including the OBJECT target
// (R2 asks 1–5, 2026-09-28: the composition-rolled object defence pass, object_damage_mitigation, exploding dice, the inventory
// identity on the row, the object's vitality write + destruction flag via ctx.objectDamage, the row's roll_meta.feedback). This
// module presents the object to it and reads the answer back — it never re-implements a damage branch (owner: no second damage
// resolver). IMPORT ONLY from Lane A's files (lanes doc §File ownership); every gap is a cross-lane ask in
// plans/janus-r2-object-damage-handoff-2026-09-27.md.
//
// What stays on THIS side (the fall wrapper, not the roll): the preamble (negate, tiers — fallTier) and the pre-draw gate for the
// janus() wrapper steps this route bypasses (objectFallJanusUnmodelled).
// Oracle: tools/probes/r2_object_fall_janus_oracle.php; unit: tools/test/janus-h3-object-fall.test.mjs §8.

import { damageTypeRoll } from '../ability/rolls/damage-type-roll.js';
import { HandBack, Refusal, type JanusCtx, type JanusReaders, type LogRow, type Row, upper } from '../ability/context.js';
import { type JanusInput } from '../ability/janus.js';
import { type RollSource } from '../actions/roll-source.js';
import { DAMAGE_BRANCH_STATUS_IDS, DAMAGE_ROLL_TYPE, FALL_ABILITY, fallTier } from './object-fall-damage.js';

/** Layers the janus() WRAPPER acts on for the object — this route calls damageTypeRoll() directly, so those wrapper steps never
 * run here: the trigger carriers (fire_caster_makes_roll_type_trigger / janus_triggers_on_hit / janus_trigger walk every carrier
 * bucket, and a live status's own layers as a carrier) and immune_to_ability (the pre-flight target gate). */
const WRAPPER_CARRIER_LAYERS: ReadonlySet<string> = new Set(['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse',
  'at_will_spell', 'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability', 'feat', 'immune_to_ability']);
const WRAPPER_FAMILY = /^triggers_/;

export interface ObjectFallRouteGateInput {
  /** Keys of the object's own layers (row + catalog definition) that carry a value. */
  ownLayerKeys: string[];
  /** Each status on the object: its item id and the held definition (null = not held). */
  statuses: Array<{ id: string; definition: { item_layer_types?: unknown } | null }>;
  /** inventory_private = 1 and the viewer is neither GM nor employee: validate_and_adjust_targets vetoes the legacy fall
   * (janus_request_explicitly_targets_private_entity — the fall targets the object by id). */
  privateToViewer: boolean;
}
/**
 * The pre-draw gate of THIS route: only what the bypassed janus() wrapper would do that damageTypeRoll() cannot see — a status PHP
 * acts on by id (stealth reveal on action, long casts, sleeping / paralyzed / charm / dying / Shielded / Mirror Image …), a trigger
 * carrier on the object or on one of its statuses, immune_to_ability, and the privacy veto. Everything the ROLL reads (roll-type
 * modifiers, defences, percentages, re-rolls, transfer, the wall run, the house rules, ADV / DIS) is Lane A's to answer or hand
 * back — the oracle holds it to "exact or pre-draw" on every case, gate or no gate (test §8, --ungated). Null = route it.
 */
export function objectFallJanusUnmodelled(input: ObjectFallRouteGateInput): string | null {
  for (const status of input.statuses) {
    const id = upper(status.id);
    if (!id || DAMAGE_BRANCH_STATUS_IDS.has(id)) return `status-branch:${id || 'unknown'}`;
    // PHP json-encodes an empty layer map as [].
    const raw = status.definition?.item_layer_types;
    const layers = Array.isArray(raw) && raw.length === 0 ? {} : raw;
    if (!status.definition || (layers !== undefined && (layers === null || typeof layers !== 'object' || Array.isArray(layers))))
      return 'status-definition-not-held';
    const carrier = Object.keys((layers ?? {}) as Row).find(key => WRAPPER_CARRIER_LAYERS.has(key) || WRAPPER_FAMILY.test(key));
    if (carrier) return `status-layer:${carrier}`;
  }
  const own = input.ownLayerKeys.find(key => WRAPPER_CARRIER_LAYERS.has(key) || WRAPPER_FAMILY.test(key));
  if (own) return `layer:${own}`;
  if (input.privateToViewer) return 'private-object';
  return null;
}

export interface ObjectFallJanusInput {
  encounter: Row;
  inventoryId: string;
  /** The object as janus() reads it: `items`, `item_layer_types` / `catalog` (own + template, as the readers resolve them),
   * `statuses.target` rows (status_item_id + status_arr), `inventory` (its inventory_generation). Never mutated. It must carry
   * NO encounter_creatures bucket (Lane A's isInventoryRow()). */
  object: Row;
  fallFt: number;
  negate: number | 'inf';
  /** Hydrated vitality (a lean roster row elides catalog defaults — memory feedback_client_truth_lean_inventory_row_hydration). */
  vitality: { vMax: number; vCurrent: number; vTemp: number; effectiveVMax: number };
  /** The catalog 'Take Fall Damage' definition ({items, item_layer_types}); dice / die are set from the fall here. */
  ability: Row;
  read: JanusReaders;
  /** Called after the dry runs, BEFORE the real draw: destructive totals let the caller preflight splash branches too.
   * A reason refuses the fall before any dice are drawn. */
  beforeDraw?: (canDestroy: boolean, breakingDamage: number[]) => string | null;
}

export type ObjectFallJanusVerdict =
  | { verdict: 'none'; reason: 'negated' | 'short' | 'no-vitality' }
  | { verdict: 'handBack'; reason: string; postDraw: boolean }
  | { verdict: 'damage'; dice: number; die: number; row: LogRow; rollsFull: unknown[]; result: number; feedback: Row;
      vCurrent: number; vTemp: number; destroyed: boolean };

/** One pass of Lane A's roll over the object. Throws HandBack (Lane A's or this module's). */
function runOnce(input: ObjectFallJanusInput, dice: number, die: number, roll: RollSource): Extract<ObjectFallJanusVerdict, { verdict: 'damage' }> {
  const id = upper(input.inventoryId);
  if (input.object.encounter_creatures !== undefined) throw new HandBack('object-fall-janus:creature-shaped-row');
  // Shallow copies of exactly what Lane A writes (computed.vitality, the inventory bucket's v_current / v_temp) — the row carries
  // the encounter catalog, which must not be deep-cloned per dry run. Caster and target are separate copies, as in PHP.
  const bucket = (input.object.inventory && typeof input.object.inventory === 'object' && !Array.isArray(input.object.inventory) ? input.object.inventory : {}) as Row;
  const v = input.vitality;
  const copy = (): Row => ({ ...input.object, inventory: { ...bucket, inventory_id: id },
    computed: { ...((input.object.computed as Row | undefined) ?? {}),
      vitality: { v_current: v.vCurrent, v_max: v.vMax, effective_v_max: v.effectiveVMax, ...(v.vTemp > 0 ? { v_temp: v.vTemp } : {}) } } });
  const object = copy();
  const ability = JSON.parse(JSON.stringify(input.ability)) as Row;
  ability.item_layer_types = { ...((ability.item_layer_types as Row | undefined) ?? {}), dice, die };
  const ctx: JanusCtx = {
    encounter: input.encounter, members: new Map(), originals: new Map(), read: input.read, roll,
    rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [], statusConnectedId: null, rollPrivate: 0,
    // CAPABILITY: this caller persists the object's damage and finishes a destruction (R1's planDamageDestruction).
    objectDamage: [],
  };
  const janusInput: JanusInput = { casterId: id, targetIds: [id], abilityId: FALL_ABILITY.id, rawDiceMod: 0, rawDiceAdvDisStacks: 0,
    useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: id };
  // PHP passes a copy of the object as the caster and the object as the target; nothing reads the caster after the roll.
  damageTypeRoll(ctx, janusInput, ability, copy(), object);
  // The fall writes exactly one damage row and the object's own vitality, nothing else.
  if (ctx.rows.length !== 1) throw new HandBack(`object-fall-janus:rows-${ctx.rows.length}`);
  if (ctx.statusesAdded.length || ctx.statusesRemoved.length || ctx.chargesChanged.length || ctx.inventoryChanged.length
      || (ctx.statusStacks ?? []).length || (ctx.concentrationBreaks ?? []).length || (ctx.deathTail ?? []).length || ctx.playerCharactersChanged?.length)
    throw new HandBack('object-fall-janus:side-effect');
  const hit = ctx.objectDamage ?? [];
  if (hit.length !== 1 || upper(hit[0]!.inventory_id) !== id) throw new HandBack('object-fall-janus:object-damage-shape');
  const row = ctx.rows[0]!;
  if (row.roll_type_1 !== DAMAGE_ROLL_TYPE || row.roll_item_id !== FALL_ABILITY.id || upper(row.roll_by) !== id || upper(row.roll_to) !== id)
    throw new HandBack('object-fall-janus:row-shape');
  if (!Array.isArray(row.rolls_full)) throw new HandBack('object-fall-janus:rolls-full');
  if (!row.feedback) throw new HandBack('object-fall-janus:feedback-not-held');
  return { verdict: 'damage', dice, die, row, rollsFull: row.rolls_full, result: row.roll_result, feedback: row.feedback as unknown as Row,
    vCurrent: hit[0]!.v_current, vTemp: hit[0]!.v_temp, destroyed: hit[0]!.destroyed };
}

/** Dry-run faces: every die at its max, every die at 1, and the two alternations (Lane A's pre-draw discipline). An exploding
 * chain draws more under the max run, so every branch the dice can steer (destroyed or not, a chain or not) is met pre-draw. */
const DRY_RUNS: ReadonlyArray<(die: number, n: number) => number> = [die => die, () => 1, (die, n) => n % 2 ? 1 : die, (die, n) => n % 2 ? die : 1];

/**
 * The fall, resolved by Lane A's roll. Every hand-back is decided BEFORE the real draw (the dry runs); one that surfaces only on
 * the real faces is reported `postDraw: true` — the caller must then NOT re-roll anywhere (memory
 * feedback_client_truth_no_handback_after_a_draw).
 */
export function objectFallThroughJanus(input: ObjectFallJanusInput, roll: RollSource): ObjectFallJanusVerdict {
  const tier = fallTier({ fallFt: input.fallFt, negate: input.negate, vMax: input.vitality.vMax });
  if (tier.verdict === 'none') return tier;
  const { dice, die } = tier;
  const caught = (error: unknown, postDraw: boolean): ObjectFallJanusVerdict => {
    if (error instanceof HandBack) return { verdict: 'handBack', reason: error.reason, postDraw };
    if (error instanceof Refusal) return { verdict: 'handBack', reason: `refusal:${error.refusal}`, postDraw };
    // A reader that throws on a shape it cannot read is unknown state, never an answer (the dry runs meet it first).
    return { verdict: 'handBack', reason: `object-fall-janus:error:${error instanceof Error ? error.message : String(error)}`, postDraw };
  };
  let canDestroy = false;
  const breakingDamage = new Set<number>();
  for (const face of DRY_RUNS) {
    // An exploding chain on an all-max run never ends by itself; PHP's own fuse stops it (24 links), and so does Lane A's.
    let n = 0;
    try {
      const dry = runOnce(input, dice, die, { d: d => face(d, n++), fraction: () => 0 });
      if (dry.destroyed) { canDestroy = true; breakingDamage.add(dry.result); }
    }
    catch (error) { return caught(error, false); }
  }
  // The caller's destruction plan can throw on a shape it cannot read (mesh / transform geometry): still pre-draw, still a hand-back.
  let veto: string | null;
  try { veto = input.beforeDraw?.(canDestroy, [...breakingDamage]) ?? null; }
  catch (error) { return caught(error, false); }
  if (veto) return { verdict: 'handBack', reason: veto, postDraw: false };
  try { return runOnce(input, dice, die, roll); }
  catch (error) { return caught(error, true); }
}
